import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from './SEOHead';

const Terms = () => {
  return (
  <>
  <SEOHead
    title="Terms of Service"
    description="Cayda terms of service. Read about usage rights, responsibilities, and policies."
    path="/terms"
  />
  <div className="min-h-screen bg-cream">
    <header className="border-b border-ink-faint/20 bg-cream/80 backdrop-blur-xl">
      <div className="max-w-3xl mx-auto px-6 h-14 flex items-center">
        <Link to="/" className="text-base font-display font-semibold tracking-tight text-ink">Cayda</Link>
      </div>
    </header>

    <main className="max-w-3xl mx-auto px-6 py-16">
      <h1 className="text-2xl font-display font-bold text-ink tracking-tight mb-2">Terms of Service</h1>
      <p className="text-xs font-display text-ink-faint mb-10">Last updated: February 2026</p>

      <div className="space-y-8 text-sm font-display text-ink-secondary leading-relaxed">
        <section>
          <h2 className="text-base font-display font-semibold text-ink mb-3">1. Acceptance of Terms</h2>
          <p>By accessing or using Cayda, a product of Slowfast LLC, a Wyoming limited liability company ("the Service"), you agree to be bound by these Terms of Service. If you are using the Service on behalf of an organization, you represent that you have the authority to bind that organization to these terms.</p>
        </section>

        <section>
          <h2 className="text-base font-display font-semibold text-ink mb-3">2. Description of Service</h2>
          <p>Cayda provides a website annotation and feedback tool that allows teams to place notes, comments, and feedback directly on live websites. The service consists of a web dashboard, an API, and an embeddable JavaScript widget.</p>
        </section>

        <section>
          <h2 className="text-base font-display font-semibold text-ink mb-3">3. Account Registration</h2>
          <p>You must provide accurate and complete information when creating an account. You are responsible for maintaining the security of your account credentials and for all activities that occur under your account. You must notify us immediately of any unauthorized use.</p>
        </section>

        <section>
          <h2 className="text-base font-display font-semibold text-ink mb-3">4. Acceptable Use</h2>
          <p className="mb-2">You agree not to use the Service to:</p>
          <p className="mb-1">• Violate any applicable laws or regulations</p>
          <p className="mb-1">• Install the widget on websites you do not own or have authorization to modify</p>
          <p className="mb-1">• Collect personal data from website visitors through the widget</p>
          <p className="mb-1">• Interfere with or disrupt the integrity or performance of the Service</p>
          <p className="mb-1">• Attempt to gain unauthorized access to the Service or its related systems</p>
          <p>• Use the Service for any purpose that is harmful, fraudulent, or deceptive</p>
        </section>

        <section>
          <h2 className="text-base font-display font-semibold text-ink mb-3">5. Widget Installation</h2>
          <p>By installing the Cayda widget on a website, you represent that you have the legal right to modify that website's code. The widget is designed to be invisible to regular website visitors and does not modify the website's content or functionality for visitors. You are responsible for informing your website's visitors of any third-party scripts installed on your site, as required by applicable law.</p>
        </section>

        <section>
          <h2 className="text-base font-display font-semibold text-ink mb-3">6. Subscription & Billing</h2>
          <p className="mb-2">Cayda offers free and paid subscription tiers. Paid subscriptions are billed per seat on a monthly or annual basis through Stripe. You authorize us to charge your payment method for the applicable subscription fees.</p>
          <p className="mb-2">You may upgrade, downgrade, or cancel your subscription at any time through the dashboard. Downgrades and cancellations take effect at the end of the current billing period. Upgrades are prorated and take effect immediately.</p>
          <p>Refunds are handled on a case-by-case basis. Contact support@cayda.io for refund requests.</p>
        </section>

        <section>
          <h2 className="text-base font-display font-semibold text-ink mb-3">7. Intellectual Property</h2>
          <p>You retain ownership of all content you create through the Service (notes, comments, screenshots). Cayda retains ownership of the Service itself, including the dashboard, API, widget code, and all related intellectual property. You grant us a limited license to use your content solely for the purpose of providing the Service.</p>
        </section>

        <section>
          <h2 className="text-base font-display font-semibold text-ink mb-3">8. Privacy</h2>
          <p>Your use of the Service is also governed by our <Link to="/privacy" className="text-ink underline underline-offset-2">Privacy Policy</Link>, which describes how we collect, use, and protect your data.</p>
        </section>

        <section>
          <h2 className="text-base font-display font-semibold text-ink mb-3">9. Service Availability</h2>
          <p>We strive to maintain high availability but do not guarantee uninterrupted access to the Service. We may perform scheduled maintenance with advance notice when possible. We are not liable for any downtime, data loss, or service interruptions.</p>
        </section>

        <section>
          <h2 className="text-base font-display font-semibold text-ink mb-3">10. Limitation of Liability</h2>
          <p>To the maximum extent permitted by law, Slowfast LLC shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of or inability to use the Service. Our total liability shall not exceed the amount you paid us in the 12 months preceding the claim.</p>
        </section>

        <section>
          <h2 className="text-base font-display font-semibold text-ink mb-3">11. Termination</h2>
          <p>We may terminate or suspend your account at any time for violation of these terms, with or without notice. Upon termination, your right to use the Service ceases immediately. You may request a data export within 30 days of termination.</p>
        </section>

        <section>
          <h2 className="text-base font-display font-semibold text-ink mb-3">12. Changes to Terms</h2>
          <p>We reserve the right to modify these terms at any time. We will notify you of material changes via email or through the Service. Continued use of the Service after changes constitutes acceptance of the new terms.</p>
        </section>

        <section>
          <h2 className="text-base font-display font-semibold text-ink mb-3">13. Governing Law</h2>
          <p>These Terms shall be governed by and construed in accordance with the laws of the State of Wyoming, without regard to its conflict of law provisions. Any disputes arising under these terms shall be resolved in the courts of the State of Wyoming.</p>
        </section>

        <section>
          <h2 className="text-base font-display font-semibold text-ink mb-3">14. Contact</h2>
          <p>Questions about these terms? Contact us at <a href="mailto:support@cayda.io" className="text-ink underline underline-offset-2">support@cayda.io</a>.</p>
        </section>
      </div>
    </main>

    <footer className="border-t border-ink-faint/20 py-6 px-6 bg-cream">
      <div className="max-w-3xl mx-auto flex items-center justify-between">
        <Link to="/" className="text-xs font-display text-ink-faint hover:text-ink-secondary transition-colors">← Back to Cayda</Link>
        <span className="text-xs font-display text-ink-faint">&copy; {new Date().getFullYear()} Slowfast LLC</span>
      </div>
    </footer>
  </div>
  </>
  );
};

export default Terms;
